import { useState, useEffect, Fragment } from "react";
import { Button, Form, Input, Select, Spin, TreeSelect } from "antd";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { useNavigate, useParams, useLocation } from "react-router";

import { ListToTree, ObjectNumbersToEnglish } from "scripts/helpers";
import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Icons from "scripts/icons";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import Upload from "components/upload";
import AddJobModal from "modals/add-job";

const MapEvent = ({ onChange }) => {
	const map = useMap();

	map.on("moveend", () => {
		const { lat, lng } = map.getCenter();

		onChange({ latitude: lat, longitude: lng, zoom: map.getZoom() });
	});

	return null;
};

const Component = () => {
	const initialState = { categories: [], cities: [], provinces: [] };
	const [state, setState] = useState(initialState);
	const { categories, cities, data, loaded, provinces, submitting } = state;
	const [form] = Form.useForm();
	const { hash, pathname, search } = useLocation();
	const { id } = useParams();
	const navigate = useNavigate();

	const handleChange = ({ latitude, longitude }) => {
		form.setFieldsValue({
			latitude: latitude.toString(),
			longitude: longitude.toString(),
		});
	};

	const handleCity = (id) => {
		API.cities(id, { paginate: false })
			.then(({ data: cities }) => {
				setState((prevState) => ({
					...prevState,
					cities: cities?.map(({ id, name }) => ({ label: name, value: id })),
				}));
			})
			.catch(() => {});
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));
				values.avatarId = values.avatar?.id;
				values.coverId = values.cover?.id;

				values = ObjectNumbersToEnglish(values);

				delete values.province;
				delete values.avatar;
				delete values.cover;

				const action = id ? API.job.update(id, values) : API.job.create(values);

				action
					.then(() => {
						navigate(`${pathname}${search}#${Modals.ADDJOB}`);
						if (id) navigate("/panel/jobs");
					})
					.finally(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		if (id) {
			Promise.all([
				API.job.select(id),
				API.category.list({ paginate: false }),
				API.provinces({ paginate: false }),
			])

				.then(([{ data: job }, { data: categories }, { data: provinces }]) => {
					setState((prevState) => ({
						...prevState,
						loaded: true,
						categories: ListToTree(categories, true, true),
						cities: [{ label: job?.city?.name, value: job?.city?.id }],
						provinces: provinces?.map(({ id, name }) => ({
							label: name,
							value: id,
						})),
					}));
					form.setFieldsValue({
						...job,
						discount: job?.discount?.raw || 0,
						province: job?.province?.id,
						cityId: job?.city?.id,
						categoryId: job?.category?.id,
						isPublished: job?.isPublished?.bool,
					});
				})
				.catch(() => {});
		} else {
			form.resetFields();
			Promise.all([
				API.category.list({ paginate: false }),
				API.provinces({ paginate: false }),
			])

				.then(([{ data: categories }, { data: provinces }]) => {
					setState((prevState) => ({
						...prevState,
						loaded: true,
						categories: ListToTree(categories, true, true),
						provinces: provinces?.map(({ id, name }) => ({
							label: name,
							value: id,
						})),
					}));
				})
				.catch(() => {});
		}
	};

	useEffect(componentDidUpdate, [id]);

	return (
		<div className="panel-create">
			<Form
				layout="vertical"
				form={form}
			>
				{loaded ? (
					<div className="row">
						<div className="col-12 col-md-6">
							<Form.Item
								name="title"
								label={`${Resources.name} ${Resources.job}`}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="ceo"
								label={`${Resources.name} ${Resources.manage}`}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="workTime"
								label={`${Resources.clock} ${Resources.working}`}
								rules={Rules.required}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="discount"
								label={Resources.discountPercent}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="categoryId"
								label={Resources.category}
								rules={Rules.required}
							>
								<TreeSelect
									treeData={categories}
									treeNodeFilterProp="label"
									showSearch
									allowClear
								/>
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="isPublished"
								label={`${Resources.status} ${Resources.publish}`}
							>
								<Select
									options={[
										{ value: true, label: Resources.published },
										{ value: false, label: Resources.notPublished },
									]}
								/>
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="province"
								label={Resources.province}
								rules={Rules.required}
							>
								<Select
									options={provinces}
									optionFilterProp="label"
									onChange={(id) => handleCity(id)}
									showSearch
								/>
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="cityId"
								label={Resources.city}
								rules={Rules.required}
							>
								<Select
									disabled={cities.length === 0}
									options={cities}
									optionFilterProp="label"
									showSearch
								/>
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="websiteUrl"
								label={`${Resources.address} ${Resources.website}`}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12 col-md-6">
							<Form.Item
								name="instagramUrl"
								label={`${Resources.address} ${Resources.page} ${Resources.instagram}`}
							>
								<Input />
							</Form.Item>
						</div>
						<div className="col-12">
							<Form.List name="phoneNumbers">
								{(fields, { add, remove }) => (
									<div className="row">
										{fields.map(({ key, name, ...restField }) => (
											<Fragment key={key}>
												<div className="col-6 col-sm-6">
													<Form.Item
														name={[name, "number"]}
														label={Resources.number}
														rules={Rules.required}
														{...restField}
													>
														<Input className="sp-input-ltr" />
													</Form.Item>
												</div>
												<div className="col-4 col-sm-5">
													<Form.Item
														name={[name, "type"]}
														label={Resources.type}
														rules={Rules.required}
														{...restField}
													>
														<Select
															options={[
																{
																	value: "mobile",
																	label: Resources.mobile,
																},
																{
																	value: "phone",
																	label: Resources.phone,
																},
																{
																	value: "fax",
																	label: Resources.fax,
																},
															]}
														/>
													</Form.Item>
												</div>
												<div className="col-2 col-sm-1">
													<Button
														type="link"
														onClick={() => remove(name)}
														block
													>
														<Icons.MinusSquare />
													</Button>
												</div>
											</Fragment>
										))}
										<div className="col-12">
											<Form.Item>
												<Button
													type="dashed"
													onClick={() => add()}
													block
												>{`${Resources.add} ${Resources.number}`}</Button>
											</Form.Item>
										</div>
									</div>
								)}
							</Form.List>
						</div>
						<div className="col-12">
							<Form.Item
								name="description"
								label={Resources.description}
							>
								<Input.TextArea rows={6} />
							</Form.Item>
						</div>
						<div className="col-12">
							<Form.Item
								name="address"
								label={Resources.address}
							>
								<Input.TextArea rows={3} />
							</Form.Item>
						</div>
						<div className="col-12">
							<Form.Item
								name="longitude"
								label={Resources.longitude}
								noStyle
							>
								<Input type="hidden" />
							</Form.Item>
							<Form.Item
								name="latitude"
								label={Resources.latitude}
								noStyle
							>
								<Input type="hidden" />
							</Form.Item>
							<MapContainer
								center={{
									lat: parseFloat(process.env.REACT_APP_LATITUDE),
									lng: parseFloat(process.env.REACT_APP_LONGITUDE),
								}}
								length={4}
								zoom={parseInt(process.env.REACT_APP_ZOOM)}
								style={{ height: 220, width: "100%" }}
							>
								<MapEvent onChange={handleChange} />
								<TileLayer
									attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
									url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
								/>
							</MapContainer>
						</div>
						<div className="col-6">
							<Form.Item
								name="avatar"
								label={`${Resources.add} ${Resources.avatar}`}
							>
								<Upload section={Types.upload.job} />
							</Form.Item>
						</div>
						<div className="col-6">
							<Form.Item
								name="cover"
								label={`${Resources.add} ${Resources.cover}`}
							>
								<Upload
									aspect={1.8}
									section={Types.upload.job}
								/>
							</Form.Item>
						</div>
						<div className="col-12">
							<Button
								type="primary"
								onClick={handleSubmit}
								loading={submitting}
							>
								{Resources.confirm}
							</Button>
						</div>
					</div>
				) : (
					<Spin className="page-loading" />
				)}
			</Form>
			<AddJobModal />
		</div>
	);
};

export default Component;
